import React from "react";
import { observer } from "mobx-react-lite";
import { Box, Button, Center, FlexProps } from "@chakra-ui/react";

import { IPathOptions, Lang } from "@noli/core";
import { langStore, translate, usePathOptions, useSelecting } from "@noli/business";

import { toast, toFixed, useOwnToast } from "@/utils/misc";
import { SafeOp } from "@/utils/safeOp";
import { Actions } from "@/utils/api";
import { themeStore } from "@/stores/theme";
import { tasksStore } from "@/stores/tasks";
import SubEditorPanel from "@/components/SubPanel/SubEditor";
import { PathEditorPanel } from "./common";
import { Editor_Words } from "@/utils/lang/editor";
import { APIs_Img2Img } from "../request/api";

type SinglePathEditorProps = FlexProps & {
  alias: string;
  lang: Lang;
  noTitle?: boolean;
  pathIndex?: number;
} & ReturnType<typeof usePathOptions>;

const SinglePathEditor: React.FC<SinglePathEditorProps> = ({
  alias,
  lang,
  noTitle,
  pathIndex,
  options,
  setOptions,
  ...others
}) => {
  if (!options || Array.isArray(options)) return null;
  const { fill, stroke, width, linecap } = options;
  const setOpt = ({ trace }: { trace: boolean }) => {
    return (opt: Partial<IPathOptions>) =>
      setOptions({ trace })({ fill, stroke, width, linecap, ...opt, index: pathIndex });
  };
  return (
    <PathEditorPanel
      alias={alias}
      {...others}
      lang={lang}
      noTitle={noTitle}
      options={options}
      setStroke={({ trace }) =>
        (stroke) =>
          setOpt({ trace })({ stroke })}
      setWidth={({ trace }) =>
        (width) =>
          setOpt({ trace })({ width })}
      setUseFill={(use) => setOpt({ trace: true })({ fill: use ? stroke : "none" })}
      setFill={({ trace }) =>
        (fill) =>
          setOpt({ trace })({ fill })}
    />
  );
};

function PathEditor(props: FlexProps) {
  const t = useOwnToast();
  const lang = langStore.tgt;
  const { options, setOptions } = usePathOptions();
  const { type, displayNode } = useSelecting("raw");
  const { separatorColor } = themeStore.styles;

  if (!options || type !== "path") {
    return null;
  }

  async function landscapeSynthesis(): Promise<void> {
    // sanity check
    if (displayNode?.type !== "path") return;
    const alias = displayNode.alias;
    if (tasksStore.busyNodes.has(alias)) {
      toast(t, "info", translate(Editor_Words["already-in-queue-message"], lang));
      return;
    }
    // setup
    const { w, h } = displayNode.wh;
    const virtualInfo = { w: toFixed(w, 2), h: toFixed(Math.abs(h), 2) };
    const virtualNodeInfo = await SafeOp.setupPostCreator(
      t,
      APIs_Img2Img.semantic2img,
      lang,
      [alias],
      virtualInfo,
    );
    const failed = async () => {
      await SafeOp.cleanupSetup([alias], virtualNodeInfo);
      toast(t, "error", translate(Editor_Words["landscape-synthesis-error-message"], lang));
    };
    // export node
    const reqBlob = await SafeOp.exportSingleNodeBlob(displayNode, {
      t,
      lang,
      compressOptions: { antialias: false },
    });
    if (!reqBlob) {
      failed();
      return;
    }
    const urls = await SafeOp.uploadImage(t, lang, reqBlob, { failed, returnWH: false });
    if (!urls) return;
    const { cdn } = urls;
    await Actions.importMeta({
      t,
      lang,
      type: "landscape",
      meta: {
        type: "landscape",
        data: {
          url: cdn,
          wh: virtualInfo,
        },
      },
      setupInfo: {
        checkBusy: false,
        busyAliases: [alias],
        virtualNodeInfo,
      },
      opt: null,
    });
  }

  const alias = displayNode.alias;
  // for landscape path, display a special UI
  if (alias.startsWith("landscape") || alias.startsWith(".c.landscape")) {
    return (
      <Box w="100%" borderTop="1px" borderColor={separatorColor}>
        <Center pt="24px">
          <Button w="80%" onClick={landscapeSynthesis}>
            {translate(Editor_Words["landscape-synthesis-submit"], lang)}
          </Button>
        </Center>
      </Box>
    );
  }

  // normal path editor
  if (!Array.isArray(options)) {
    return (
      <SinglePathEditor
        alias={alias}
        {...props}
        lang={lang}
        options={options}
        setOptions={setOptions}
      />
    );
  }
  return (
    <SubEditorPanel w="100%" pb="32px">
      {options.map((opt, i) => (
        <SinglePathEditor
          alias={`${alias}.${i}`}
          {...props}
          key={i}
          lang={lang}
          noTitle={i !== 0}
          pathIndex={i}
          options={opt}
          setOptions={setOptions}
        />
      ))}
    </SubEditorPanel>
  );
}

export default observer(PathEditor);
